import { Component, Inject, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import {
  ArchiveFile,
  ArchiveStore,
  BackNavigable,
  BackNavigationService,
  Command,
  CommandStore,
  SOFTLINE_FEATURE_ARCHIVE,
  SOFTLINE_FEATURE_COMMANDS,
  SOFTLINE_FEATURE_TITLE,
  TitleStore } from '@softline/application';
import { BehaviorSubject, combineLatest, Subscription } from 'rxjs';
import { SOFTLINE_SERVICE_UUID, Store } from '@softline/core';
import { Router } from '@angular/router';
import { SOFTLINE_FEATURE_UPLOAD_ARCHIVE_VOUCHER } from '../../archivbeleg-upload.shared';
import { UploadArchiveVoucherStore } from '../../store/voucher.store';
import { map } from 'rxjs/operators';
import {
  MessageBarStore,
  SOFTLINE_FEATURE_MESSAGE_BAR } from '@softline/ui-core';
import { CommonModule } from '@angular/common';

import { FileInputComponent } from '@softline/ui-core';
import { FormatPipe } from '@softline/ui-core';
import { TranslatePipe } from '@softline/ui-core';
@Component({
    selector: 'lib-upload-picture',
    templateUrl: './upload-picture.component.html',
    styleUrls: ['./upload-picture.component.css'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [CommonModule,
    FileInputComponent,
    FormatPipe,
    TranslatePipe]
})
export class UploadPictureComponent
  implements OnInit, OnDestroy, BackNavigable
{
  private subscription?: Subscription;

  voucher$ = this.store.observe(
    SOFTLINE_FEATURE_UPLOAD_ARCHIVE_VOUCHER,
    UploadArchiveVoucherStore.getters.data
  );
  loading$ = new BehaviorSubject<boolean>(false);

  uploads$ = new BehaviorSubject<{ file: File; token: string }[]>([]);
  uploadState$ = combineLatest([
    this.uploads$,
    this.store.observe<ArchiveStore.State>(SOFTLINE_FEATURE_ARCHIVE)]).pipe(
    map(([uploads, archive]) => {
      return uploads.map((upload) => {
        const state = archive.actions[upload.token];
        const progress = archive.progresses[upload.token];
        return {
          file: upload.file,
          state: state?.state ?? 'pending',

          uploaded: progress?.loaded ?? 0,
          total: progress?.total ?? upload.file.size };
      });
    })
  );

  constructor(
    private store: Store,
    protected router: Router,
    @Inject(SOFTLINE_SERVICE_UUID) private uuid: () => string,
    private backNavigationService: BackNavigationService
  ) {}

  ngOnInit(): void {
    this.backNavigationService.set(this);
    this.store.commit(
      SOFTLINE_FEATURE_COMMANDS,
      CommandStore.mutations.addSet,
      {
        name: UploadPictureComponent,
        commands: this.createCommands() }
    );
    this.store.commit(
      SOFTLINE_FEATURE_TITLE,
      TitleStore.mutations.setTitle,
      '#UPLOAD_ARCHIVE_VOUCHER.UPLOAD_PICTURE.TITLE'
    );
    this.subscription = this.store
      .observe(
        SOFTLINE_FEATURE_UPLOAD_ARCHIVE_VOUCHER,
        UploadArchiveVoucherStore.getters.data
      )
      .subscribe((data) => {
        if (!data) this.router.navigate(['/archivbeleg']);
      });
  }

  async ngOnDestroy(): Promise<void> {
    this.backNavigationService.set(undefined);
    if (this.subscription && !this.subscription.closed)
      this.subscription.unsubscribe();
    this.subscription = undefined;

    this.store.commit(
      SOFTLINE_FEATURE_TITLE,
      TitleStore.mutations.setTitle,
      ''
    );
    this.store.commit(
      SOFTLINE_FEATURE_UPLOAD_ARCHIVE_VOUCHER,
      UploadArchiveVoucherStore.mutations.unset
    );
    this.store.commit(
      SOFTLINE_FEATURE_COMMANDS,
      CommandStore.mutations.removeSet,
      UploadPictureComponent
    );
  }

  protected createCommands(): Command[] {
    return [
      {
        icon: 'fa-regular fa-file-arrow-up',
        name: '#UPLOAD_ARCHIVE_VOUCHER.UPLOAD_PICTURE.TITLE',
        class: 'menu main-menu main-menu-top',
        routerLink: ['/archivbeleg'] }];
  }

  async navigateBack(): Promise<void> {
    await this.router.navigate(['archivbeleg']);
  }

  async onUpload(archiveKey: string, files: File[]): Promise<void> {
    this.loading$.next(true);
    const succeeded: ArchiveFile[] = [];
    const failed: File[] = [];
    for (const file of files) {
      try {
        const token = this.uuid();
        const result = await this.store.dispatch(
          SOFTLINE_FEATURE_ARCHIVE,
          ArchiveStore.actions.upload,
          {
            archiveKey,
            files: [file],
            token }
        );
        this.uploads$.next([...this.uploads$.value, { file, token }]);
        succeeded.push(result);
      } catch (e) {
        failed.push(file);
      }
    }
    this.loading$.next(false);
    this.onUploadCompleted(succeeded, failed);
  }

  onUploadCompleted(succeeded: ArchiveFile[], failed: File[]): void {
    if (!failed.length)
      this.store.dispatch(
        SOFTLINE_FEATURE_MESSAGE_BAR,
        MessageBarStore.actions.success,
        '#APPLICATION.UPLOAD.MESSAGES.ALL_SUCCEEDED.MESSAGE'
      );
    else if (!succeeded.length)
      this.store.dispatch(
        SOFTLINE_FEATURE_MESSAGE_BAR,
        MessageBarStore.actions.error,
        '#APPLICATION.UPLOAD.MESSAGES.ALL_FAILED.MESSAGE'
      );
    else
      this.store.dispatch(
        SOFTLINE_FEATURE_MESSAGE_BAR,
        MessageBarStore.actions.warning,
        '#APPLICATION.UPLOAD.MESSAGES.SOME_FAILED.MESSAGE'
      );

    if (!failed.length) {
      this.navigateBack();
    }
  }
}
